import React, { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, LayoutGrid, Sparkles } from 'lucide-react';

import { AmbientSubjectBackground } from '../studio/components/AmbientSubjectBackground';
import { CreateMaterialChooser } from '../studio/components/CreateMaterialChooser';
import { CourseMaterialPreviewPane } from '../studio/components/course/CourseMaterialPreviewPane';
import { StudioCreateModal } from '../studio/components/StudioCreateModal';
import { SubjectResourceLibrary } from '../studio/components/SubjectResourceLibrary';
import { resourceKey } from '../studio/lib/studioResourceLinks';
import { countResourcesForSubject } from '../studio/studioResources';
import type { StudioResource } from '../studio/studioResources';
import {
  getSubjectById,
  getSubjectLucideIcon,
} from '../studio/constants/subjectCatalog';
import type { StudioCreateResourceType } from '../studio/constants/studioCreateConstants';

type MaterialsMode = 'library' | 'create';

export function StudioSubjectPage() {
  const { subjectId = '' } = useParams<{ subjectId: string }>();
  const subject = getSubjectById(subjectId);
  const resourceCount = countResourcesForSubject(subjectId);
  const [materialsMode, setMaterialsMode] = useState<MaterialsMode>(
    resourceCount > 0 ? 'library' : 'create',
  );
  const [selectedResource, setSelectedResource] = useState<StudioResource | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [defaultResourceType, setDefaultResourceType] = useState<
    StudioCreateResourceType | undefined
  >('guide');

  const handleSelectResource = useCallback((resource: StudioResource) => {
    setSelectedResource(resource);
  }, []);

  const openCreate = (resourceType?: StudioCreateResourceType) => {
    setDefaultResourceType(resourceType ?? 'guide');
    setCreateOpen(true);
  };

  if (!subject || subject.id === 'general') {
    return (
      <div className="studio-page w-full min-w-0">
        <p className="text-[var(--sala-ink-muted)]">Asignatura no encontrada.</p>
        <Link to="/studio" className="sala-btn-outline mt-4 inline-flex">
          Volver al Studio
        </Link>
      </div>
    );
  }

  const SubjectIcon = getSubjectLucideIcon(subject.id);
  const tallerHref = `/studio/herramientas?subject=${subject.id}`;

  return (
    <div className="studio-page relative w-full min-w-0">
      <AmbientSubjectBackground
        subjectId={subject.id}
        subjectHue={subject.hue}
        subjectTint={subject.tint}
      />
      <div
        className="relative z-[1] w-full min-w-0"
        data-subject-id={subject.id}
        style={
          {
            '--subject-hue': subject.hue,
            '--subject-tint': subject.tint,
          } as React.CSSProperties
        }
      >
        <Link to="/studio" className="studio-course-breadcrumb">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Studio
        </Link>

        <header className="studio-course-hero">
          <div
            className="studio-course-hero__icon"
            style={{
              background: subject.tint,
              border: `1px solid color-mix(in srgb, ${subject.hue} 35%, transparent)`,
              color: subject.hue,
            }}
            aria-hidden
          >
            <SubjectIcon className="h-7 w-7" strokeWidth={2} />
          </div>
          <div className="studio-course-hero__lead min-w-0 flex-1">
            <p className="studio-course-hero__kicker">Asignatura · biblioteca</p>
            <div className="studio-course-hero__title-row">
              <h1 className="studio-course-hero__title">{subject.name}</h1>
            </div>
            <p className="mt-2 max-w-2xl text-sm text-[var(--sala-ink-muted)]">
              {subject.description}
            </p>
            {resourceCount > 0 ? (
              <div className="studio-course-hero__tags">
                <span className="studio-soft-chip">
                  {resourceCount} recurso{resourceCount === 1 ? '' : 's'}
                </span>
              </div>
            ) : null}
          </div>
        </header>

        <nav className="studio-course-section-bar" aria-label="Secciones de la asignatura">
          <button
            type="button"
            className="studio-course-section-bar__btn studio-course-section-bar__btn--active"
            aria-current="page"
          >
            <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
            Materiales
          </button>
        </nav>

        <div className="studio-course-content">
          <div className="course-workspace">
            <div className="course-workspace__toolbar">
              <div className="course-workspace__modes" role="tablist" aria-label="Modo de materiales">
                <button
                  type="button"
                  role="tab"
                  aria-selected={materialsMode === 'library'}
                  className={`course-workspace__mode${
                    materialsMode === 'library' ? ' course-workspace__mode--active' : ''
                  }`}
                  onClick={() => setMaterialsMode('library')}
                >
                  <FileText className="h-3.5 w-3.5" aria-hidden />
                  Biblioteca
                  {resourceCount > 0 ? (
                    <span className="course-workspace__mode-count">{resourceCount}</span>
                  ) : null}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={materialsMode === 'create'}
                  className={`course-workspace__mode${
                    materialsMode === 'create' ? ' course-workspace__mode--active' : ''
                  }`}
                  onClick={() => setMaterialsMode('create')}
                >
                  <Sparkles className="h-3.5 w-3.5" aria-hidden />
                  Crear
                </button>
              </div>
            </div>

            {materialsMode === 'library' ? (
              <section className="course-materials-workspace">
                <aside className="course-materials-workspace__list">
                  <div className="course-materials-sidebar">
                    <header className="course-materials-sidebar__head">
                      <div className="min-w-0 flex-1">
                        <p className="course-materials-sidebar__title">Biblioteca de {subject.name}</p>
                        <p className="course-materials-sidebar__meta">
                          {resourceCount === 0
                            ? 'Sin materiales aún'
                            : `${resourceCount} material${resourceCount === 1 ? '' : 'es'}`}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="course-workspace__cta course-workspace__cta--compact"
                        onClick={() => openCreate('guide')}
                      >
                        <Sparkles className="h-3.5 w-3.5" aria-hidden />
                        Generar con IA
                      </button>
                    </header>
                    <SubjectResourceLibrary
                      subjectId={subjectId}
                      subjectHue={subject.hue}
                      selectionMode
                      selectedKey={selectedResource ? resourceKey(selectedResource) : null}
                      onSelectResource={handleSelectResource}
                      onNewMaterial={() => openCreate('guide')}
                      newMaterialLabel="Generar con IA"
                    />
                  </div>
                </aside>
                <div className="course-materials-workspace__preview">
                  <CourseMaterialPreviewPane
                    resource={selectedResource}
                    subjectId={subject.id}
                    subjectTag={subject.tag}
                  />
                </div>
              </section>
            ) : (
              <CreateMaterialChooser
                subjectLabel={subject.name}
                onGenerate={openCreate}
                tallerHref={tallerHref}
              />
            )}
          </div>
        </div>
      </div>

      <StudioCreateModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        subjectId={subjectId}
        subjectLabel={subject.name}
        defaultResourceType={defaultResourceType}
        initialStep="intencion"
      />
    </div>
  );
}
